import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import PageMeta from '@/components/base/PageMeta';
import { getArticleById, Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const [article, setArticle] = useState<Article | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  useEffect(() => {
    if (articleId) {
      const found = getArticleById(articleId);
      setArticle(found || null);
    }
  }, [articleId]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 md:pt-28">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <p className="text-foreground-500">文章未找到</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categorySlug = categories.find((c) => c.id === article.categoryId)?.slug || 'quality';
  const pageUrl = `https://jiujiujiujiu.com/article/${article.id}`;

  return (
    <PageMeta
      title={`${article.title} - 久久久久`}
      description={article.summary}
      keywords={`久久久久,${article.tags.join(',')},持久体验,稳定性能`}
      canonicalUrl={pageUrl}
      ogType="article"
      ogImage={article.imageUrl}
      publishedTime={article.publishDate}
      author={article.author}
      tags={article.tags}
    >
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jiujiujiujiu.com' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://jiujiujiujiu.com/category/${categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: pageUrl },
        ],
      }} />

      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.summary,
        image: article.imageUrl,
        datePublished: article.publishDate,
        author: { '@type': 'Person', name: article.author },
        publisher: {
          '@type': 'Organization',
          name: '久久久久',
        },
        url: pageUrl,
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article className="pt-24 pb-10 md:pt-28 md:pb-14">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${categorySlug}` },
                  { label: article.title },
                ]}
              />

              <div className="mt-6">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-4">
                  {article.categoryName}
                </span>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-xs text-foreground-400 mb-8 flex-wrap">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.publishDate}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>
              </div>

              <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-8 pt-8 border-t border-background-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-foreground-400">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full border border-background-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-background-200">
                <Link
                  to={`/category/${categorySlug}`}
                  className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{article.categoryName}分类
                </Link>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </PageMeta>
  );
}